import { useMutation } from "@apollo/client";
import { CloudSteps } from "components/atoms/stepper/CloudStepper";
import { CloudDialogProps } from "components/modules/CloudDialog";
import { CloudStepsDialog } from "components/modules/CloudStepsDialog";
import { OrgNameAndCountry } from "components/modules/createOrganizationSteps/OrgNameAndCountry";
import { OrgOwnerEmail } from "components/modules/createOrganizationSteps/OrgOwnerEmail";
import { OrgSupportLinks } from "components/modules/createOrganizationSteps/OrgSupportLinks";
import { useFormik } from "formik";
import { graphql } from "gql";
import { CountryCodes, RoleType } from "gql/graphql";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { ORG_LIST_QUERY } from "../Organizations";
import { UPDATE_ORG } from "../panels/EditOrganizationBasicInfo";
import { INVITE } from "./InviteUser";

const CREATE_ORG = graphql(`
  mutation CreateOrganization($payload: CreateOrganizationPayload!) {
    createOrganization(payload: $payload) {
      id
      name
    }
  }
`);

export const CreateOrganization = ({ open, onClose }: CloudDialogProps) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [stepIndex, setStepIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  const handleClose = () => {
    formikOrgNameAndCountry.resetForm();
    formikOrgOwner.resetForm();
    formikOrgSupportLinks.resetForm();

    setErrorMessage("");

    setStepIndex(0);
    onClose();
  };

  const handleSetStepIndex = (newIndex: number) => {
    setStepIndex(newIndex);
    setErrorMessage("");
  };

  const [createOrganization, { loading: creatingOrganization }] = useMutation(
    CREATE_ORG,
    {
      refetchQueries: [
        {
          query: ORG_LIST_QUERY,
        },
      ],
    },
  );

  const [inviteOwner, { loading: invitingOwner }] = useMutation(INVITE, {
    refetchQueries: [
      {
        query: ORG_LIST_QUERY,
      },
    ],
  });

  const [updateOrganization, { loading: updatingOrganization }] =
    useMutation(UPDATE_ORG);

  const formikOrgNameAndCountry = useFormik<{
    orgName: string;
    country?: CountryCodes;
  }>({
    initialValues: {
      orgName: "",
      country: undefined,
    },
    validationSchema: Yup.object({
      orgName: Yup.string().required(
        intl.formatMessage({
          id: "required",
          defaultMessage: "Required",
        }),
      ),
      country: Yup.string(),
    }),
    onSubmit() {
      handleSetStepIndex(1);
    },
  });

  const formikOrgOwner = useFormik<{ email?: string }>({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required(""),
    }),
    onSubmit() {
      handleSetStepIndex(2);
    },
  });

  const formikOrgSupportLinks = useFormik<{
    odooLink?: string;
    hubspotLink?: string;
    confluenceLink?: string;
  }>({
    initialValues: {
      odooLink: "",
      hubspotLink: "",
      confluenceLink: "",
    },
    validationSchema: Yup.object({
      odooLink: Yup.string().url("Must be a valid URL"),
      hubspotLink: Yup.string().url("Must be a valid URL"),
      confluenceLink: Yup.string().url("Must be a valid URL"),
    }),
    onSubmit() {
      const snackbarId = enqueueSnackbar("Finishing organization...", {
        variant: "loading",
        persist: true,
      });

      // Handle org creation
      console.info("Creating org", formikOrgNameAndCountry.values);
      createOrganization({
        variables: {
          payload: {
            name: formikOrgNameAndCountry.values.orgName,
            country: formikOrgNameAndCountry.values.country,
          },
        },
        onCompleted: (data) => {
          // Handle owner invite
          if (formikOrgOwner.values.email) {
            console.info("Inviting owner", formikOrgOwner.values.email);
            inviteOwner({
              variables: {
                inviteInputs: [
                  {
                    email: formikOrgOwner.values.email || "",
                    role: RoleType.Owner,
                  },
                ],
                orgId: data.createOrganization.id,
              },
              onError: (e) => {
                console.warn("Could not save organization owner", e);
                setErrorMessage("Could not save organization owner");
              },
            });
          }

          // Handle support links
          console.info("Saving support links", formikOrgSupportLinks.values);
          updateOrganization({
            variables: {
              orgId: data.createOrganization.id || "",
              payload: {
                customerPage: formikOrgSupportLinks.values.odooLink?.trim(),
                customerRecordPage:
                  formikOrgSupportLinks.values.confluenceLink?.trim(),
                hubSpotPage: formikOrgSupportLinks.values.hubspotLink?.trim(),
              },
            },
            onCompleted: () => {
              closeSnackbar(snackbarId);
              enqueueSnackbar(`Created ${data.createOrganization.name}`, {
                variant: "success",
                actionLabel: "Go to organization page",
                onActionClick: () =>
                  navigate(
                    `/cloud/support/organizations/${data.createOrganization.id}`,
                  ),
              });
              handleClose();
            },
            onError: (e) => {
              console.warn("Could not save support links", e);
              enqueueSnackbar("Could not save support links", {
                variant: "error",
              });
              closeSnackbar(snackbarId);
            },
          });
        },
        onError: (e) => {
          const statusCode = e.graphQLErrors[0].extensions.status;
          closeSnackbar(snackbarId);

          console.warn("Failed to create org", e);
          handleSetStepIndex(0);

          setErrorMessage(
            statusCode === 409
              ? "Organization name already exists"
              : "Could not create organization",
          );
        },
      });
    },
  });

  const steps = [
    {
      hidden: false,
      label: "Create organization",
      component: () => (
        <OrgNameAndCountry
          formik={formikOrgNameAndCountry}
          disableForm={creatingOrganization}
          onChange={() => setErrorMessage("")}
        />
      ),
      onSubmit: formikOrgNameAndCountry.handleSubmit,
      submitLabel: "Create",
      submitDisabled:
        creatingOrganization || formikOrgNameAndCountry.isValid === false,
      loading: creatingOrganization,
    },
    {
      hidden: false,
      label: "Invite owner",
      component: () => (
        <OrgOwnerEmail formik={formikOrgOwner} disableForm={invitingOwner} />
      ),
      onSubmit: formikOrgOwner.handleSubmit,
      submitLabel: "Invite",
      secondaryButtonLabel: "Invite Owner later",
      onSecondaryButtonClick: () => {
        formikOrgOwner.resetForm();
        handleSetStepIndex(2);
      },
      submitDisabled: invitingOwner || formikOrgOwner.isValid === false,
      loading: invitingOwner,
    },
    {
      hidden: false,
      label: "Add links",
      component: () => (
        <OrgSupportLinks
          formik={formikOrgSupportLinks}
          disableForm={updatingOrganization}
        />
      ),
      onSubmit: formikOrgSupportLinks.handleSubmit,
      secondaryButtonLabel: "Add links later",
      onSecondaryButtonClick: formikOrgSupportLinks.handleSubmit,
      submitLabel: "Add",
      submitDisabled:
        Object.values(formikOrgSupportLinks.values).filter(
          (item) => item === "",
        ).length === Object.values(formikOrgSupportLinks.values).length ||
        updatingOrganization ||
        formikOrgSupportLinks.isValid === false,
      loading: updatingOrganization,
    },
  ].filter((item) => !item.hidden) as CloudSteps;

  useEffect(() => {
    formikOrgNameAndCountry.validateForm();
    formikOrgOwner.validateForm();
    formikOrgSupportLinks.validateForm();
  }, [stepIndex]);

  return (
    <CloudStepsDialog
      header="New organization"
      open={open}
      onClose={handleClose}
      hideCancel
      steps={steps}
      stepIndex={stepIndex}
      errorMessage={errorMessage}
    />
  );
};
